<template>
  <div>
    <back-link
      programmatic
      class="back-link"
      :routes="backLinkRoutes"
      :fallback="{name: 'app-details', params: { uuid: this.universalObjectId }}"
    >
      {{ $t('GO_BACK') }}
    </back-link>

    <div class="section" v-if="isPending">
      <h1 class="heading">{{ $t('OFFICIAL_APP_DOWNLOAD_PENDING_HEADING') }}</h1>
      <v-message type="warning" class="section__message">{{ $t('OFFICIAL_APP_DOWNLOAD_PENDING_WARNING') }}</v-message>
      <v-loader class="section__loader" />
    </div>

    <div v-if="errorMessage" class="section">
      <v-message type="error">{{ errorMessage }}</v-message>
    </div>

    <div class="section" v-if="downloadUri">
      <v-message type="success" class="section__message">{{ $t('OFFICIAL_APP_DOWNLOAD_SUCCESS_MESSAGE') }}</v-message>
      <v-button
        class="section__button"
        tag="a"
        target="_blank"
        size="lg"
        type="primary"
        :href="downloadUri"
      >
        {{ $t('DOWNLOAD') }}
      </v-button>
    </div>

    <commands-table class="commands" :commands="commands" />

    <html-with-links class="notice" tag="p" :html="$t('OFFICIAL_APP_DOWNLOAD_NOTICE', {faqLink: '/my/faq'})" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import BackLink from '@/components/BackLink.vue';
import CommandsTable from '@/components/CommandsTable.vue';
import VMessage from '@/components/VMessage.vue';
import VButton from '@/components/Button.vue';
import VLoader from '@/components/Loader';
import HtmlWithLinks from '@/components/HtmlWithLinks.vue';

export default {
  components: {
    BackLink,
    CommandsTable,
    VLoader,
    VMessage,
    VButton,
    HtmlWithLinks,
  },
  data() {
    return {
      isPending: true,
      commands: [],
      fetchTimeout: null,
      backLinkRoutes: ['app-details', 'my-installation-history'],
    };
  },
  computed: {
    ...mapGetters(['isStandaloneApp', 'standaloneAppId']),
    title() {
      return this.$t('OFFICIAL_APP_DOWNLOAD_PAGE_TITLE');
    },
    commandUuid() {
      return this.$route.query.commandUuid;
    },
    universalObjectId() {
      if (this.isStandaloneApp) {
        return this.standaloneAppId;
      }

      return this.$route.params.id;
    },
    errorMessage() {
      const failedCommand = this.commands.find(command => command.status.includes('failed'));
      if (!failedCommand) {
        return;
      }

      return failedCommand.params?.error || this.$t('SOMETHING_WENT_WRONG');
    },
    downloadUri() {
      const downloadCommand = this.commands.find(command => command.status === 'ok' && command.params.download_uri);
      if (!downloadCommand) {
        return;
      }

      return downloadCommand.params?.download_uri;
    },
  },
  methods: {
    async fetchCommands() {
      if (!this.isPending) {
        return;
      }

      try {
        this.commands = await this.$api.getDeviceStatus({ sinceUuid: this.commandUuid });
        this.fetchTimeout = setTimeout(this.fetchCommands, 1000);
      } catch {
        this.isPending = false;
        this.$store.dispatch('router/back', { routes: this.backLinkRoutes });
      }
    },
    validateQuery() {
      if (!this.commandUuid) {
        this.isPending = false;
        this.$store.dispatch('router/back', { routes: this.backLinkRoutes });
      }
    },
    handleDataChange() {
      if (this.errorMessage || this.downloadUri) {
        this.isPending = false;
      }
    },
    handleBeforeUnload(e) {
      e.preventDefault();
    },
  },
  watch: {
    errorMessage() {
      this.handleDataChange();
    },
    downloadUri() {
      this.handleDataChange();
    },
    isPending() {
      if (!this.isPending) {
        window.removeEventListener('beforeunload', this.handleBeforeUnload);
        clearTimeout(this.fetchTimeout);
      }
    },
  },
  created() {
    window.addEventListener('beforeunload', this.handleBeforeUnload);
    this.fetchCommands();
    this.validateQuery();
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    clearTimeout(this.fetchTimeout);
  },
  async beforeRouteLeave(to, from, next) {
    if (!this.isPending) {
      return next();
    }

    try {
      await this.$confirm(this.$t('OFFICIAL_APP_DOWNLOAD_LEAVE_CONFIRMATION'), { okText: this.$t('YES') });
      next();
    } catch {
      next(false);
    }
  },
};
</script>

<style lang="scss" scoped>
.heading {
  font-size: 16px;
  font-weight: 600;
  text-align: center;
  margin-bottom: 10px;
}

.back-link {
  margin-bottom: 15px;
}

.section {
  text-align: center;
  margin-bottom: 8px;

  &__message {
    margin-bottom: 8px;
  }

  &__loader {
    margin-top: 16px;
  }
}

.commands {
  margin-top: 16px;
}

.notice {
  margin-top: 16px;
}
</style>
